import { useState, useEffect, useRef, useCallback } from 'react';
import {
    IndexSymbol,
    SUPPORTED_SYMBOLS,
    type IndexConstituentsSummary
} from '../config/constants';
import {
    fetchAllConstituentQuotes,
    computeIndexSummary
} from '../services/liveConstituentsService';

export interface UseIndexConstituentsLiveResult {
    summaries: Record<IndexSymbol, IndexConstituentsSummary>;
    lastUpdated: Date | null;
    isLoading: boolean;
    isLive: boolean;
    refresh: () => Promise<void>;
}

const DEFAULT_SUMMARY = (sym: IndexSymbol): IndexConstituentsSummary => ({
    index: sym,
    displayName: sym === IndexSymbol.BANKNIFTY ? 'BANK NIFTY' : sym,
    exchange: sym === IndexSymbol.SENSEX ? 'BSE' : 'NSE',
    totalStocks: sym === IndexSymbol.NIFTY ? 50 : sym === IndexSymbol.BANKNIFTY ? 12 : 30,
    totalDayChange: 0,
    avgDayChangePercent: 0,
    advances: 0,
    declines: 0,
    unchanged: 0,
    top5Gainers: [],
    top5Losers: [],
    stocks: [],
    lastUpdated: null,
    isLoading: true,
});

export function useIndexConstituentsLive(intervalMs: number = 2000): UseIndexConstituentsLiveResult {
    const [summaries, setSummaries] = useState<Record<IndexSymbol, IndexConstituentsSummary>>(() => ({
        [IndexSymbol.NIFTY]: DEFAULT_SUMMARY(IndexSymbol.NIFTY),
        [IndexSymbol.BANKNIFTY]: DEFAULT_SUMMARY(IndexSymbol.BANKNIFTY),
        [IndexSymbol.SENSEX]: DEFAULT_SUMMARY(IndexSymbol.SENSEX),
    }));

    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const isFetchingRef = useRef<boolean>(false);

    const fetchLiveFeed = useCallback(async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;

        try {
            const quotesMap = await fetchAllConstituentQuotes();
            if (quotesMap.size > 0) {
                const newSummaries: Record<IndexSymbol, IndexConstituentsSummary> = {
                    [IndexSymbol.NIFTY]: computeIndexSummary(IndexSymbol.NIFTY, quotesMap),
                    [IndexSymbol.BANKNIFTY]: computeIndexSummary(IndexSymbol.BANKNIFTY, quotesMap),
                    [IndexSymbol.SENSEX]: computeIndexSummary(IndexSymbol.SENSEX, quotesMap),
                };

                setSummaries(newSummaries);
                setLastUpdated(new Date());
            }
        } catch (err) {
            console.warn('[useIndexConstituentsLive] Polling error:', err);
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        // Initial fetch
        fetchLiveFeed();

        // 2-second background polling
        const timerId = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                fetchLiveFeed();
            }
        }, intervalMs);

        // Resume immediately when tab becomes visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchLiveFeed();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(timerId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchLiveFeed, intervalMs]);

    return {
        summaries,
        lastUpdated,
        isLoading,
        isLive: true,
        refresh: fetchLiveFeed,
    };
}
